import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { StepShell, FormField } from "./StepShell";

const EMPTY_REFERENCE = {
  name: "",
  relationship: "",
  address: "",
  phone: "",
  email: "",
  remarks: "",
};

const MAX_REFERENCES = 2;

function createLocalId() {
  return `portal-reference-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function normalizeReference(reference = {}) {
  return {
    ...EMPTY_REFERENCE,
    ...reference,
    _portalId: reference._portalId || reference.id || createLocalId(),
  };
}

function compactReference(reference) {
  const {
    _portalId,
    id,
    applicationId,
    displayOrder,
    createdAt,
    updatedAt,
    ...data
  } = reference;

  return {
    name: data.name || undefined,
    relationship: data.relationship || undefined,
    address: data.address || undefined,
    phone: data.phone || undefined,
    email: data.email || undefined,
    remarks: data.remarks || undefined,
  };
}

function isReferenceComplete(reference) {
  return (
    String(reference?.name || "").trim() !== "" &&
    String(reference?.relationship || "").trim() !== "" &&
    String(reference?.address || "").trim() !== "" &&
    String(reference?.phone || "").trim() !== ""
  );
}

function areReferencesComplete(references) {
  return references.length === 2 && references.every(isReferenceComplete);
}

export default function ReferencesStep({ state, patch, setStep }) {
  const [references, setReferences] = useState(() =>
    Array.isArray(state?.references)
      ? state.references.map(normalizeReference)
      : [],
  );

  useEffect(() => {
    setReferences(
      Array.isArray(state?.references)
        ? state.references.map(normalizeReference)
        : [],
    );
  }, [state?.references]);

  const add = () => {
    setReferences((current) => {
      if (current.length >= MAX_REFERENCES) {
        return current;
      }
      return [
        ...current,
        {
          ...EMPTY_REFERENCE,
          _portalId: createLocalId(),
        },
      ];
    });
  };

  const remove = (id) => {
    setReferences((current) =>
      current.filter((reference) => reference._portalId !== id),
    );
  };

  const update = (id, field, value) => {
    setReferences((current) =>
      current.map((reference) =>
        reference._portalId === id
          ? {
              ...reference,
              [field]: value,
            }
          : reference,
      ),
    );
  };

  const canContinue = areReferencesComplete(references);

  const save = async () => {
    if (!areReferencesComplete(references)) {
      return;
    }

    await patch("references", references.map(compactReference));

    await setStep(5);
  };

  return (
    <StepShell
      testId="portal-references"
      title="References"
      subtitle="Provide details of 2 references who can be contacted regarding your application. Providing 2 references is mandatory."
      onBack={() => setStep(3)}
      onNext={save}
      nextLabel="Save & Continue"
      disableNext={!canContinue}
    >
      {references.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No references added.
        </div>
      )}

      <div className="space-y-5">
        {references.map((reference, index) => (
          <div
            key={reference._portalId}
            data-testid={`ref-${index}`}
            className="rounded-2xl border border-gray-200 p-5 bg-[#F8FAFC]"
          >
            <div className="flex justify-between items-center mb-4">
              <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-500">
                Reference #{index + 1}
              </span>

              <button
                type="button"
                onClick={() => remove(reference._portalId)}
                className="text-xs text-red-500"
              >
                Remove
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Name" required>
                <Input
                  value={reference.name}
                  onChange={(event) =>
                    update(reference._portalId, "name", event.target.value)
                  }
                />
              </FormField>

              <FormField label="Relationship" required>
                <Input
                  value={reference.relationship}
                  onChange={(event) =>
                    update(
                      reference._portalId,
                      "relationship",
                      event.target.value,
                    )
                  }
                />
              </FormField>

              <FormField label="Address" className="md:col-span-2" required>
                <Textarea
                  value={reference.address}
                  onChange={(event) =>
                    update(reference._portalId, "address", event.target.value)
                  }
                  rows={3}
                />
              </FormField>

              <FormField label="Phone" required>
                <Input
                  value={reference.phone}
                  inputMode="numeric"
                  maxLength={10}
                  onChange={(event) =>
                    update(
                      reference._portalId,
                      "phone",
                      event.target.value.replace(/\D/g, "").slice(0, 10),
                    )
                  }
                />
              </FormField>

              <FormField label="Email">
                <Input
                  type="email"
                  value={reference.email}
                  onChange={(event) =>
                    update(reference._portalId, "email", event.target.value)
                  }
                />
              </FormField>

              <FormField label="Remarks" className="md:col-span-2">
                <Textarea
                  value={reference.remarks}
                  onChange={(event) =>
                    update(reference._portalId, "remarks", event.target.value)
                  }
                  rows={3}
                />
              </FormField>
            </div>
          </div>
        ))}
      </div>

      {references.length < MAX_REFERENCES && (
        <button
          type="button"
          onClick={add}
          className="mt-5 rounded-full border px-5 py-2 text-sm font-medium"
        >
          Add Reference
        </button>
      )}
    </StepShell>
  );
}
